/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery, useQueryClient} from 'react-query';
import isNil from 'lodash.isnil';
import get from '@ion/core/src/environment/environment';
import {getAccountExtendedAttributes, saveAccountExtendedAttributes} from '@ion/core/src/services/Connect';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {
  createQueryResult,
  DurationResult,
  getStaleTime,
  onClientErrorHandler
} from '../utils/helpers';

export type UseAutojobsAttributeProps = {
  accountId: number;
  enabled?: boolean;
  page?: string;
  staleTime?: number | DurationResult;
};

export type UseAutojobsAttributeResult = {
  autojobsEnabled: boolean;
  autojobsLoading: boolean;
  autojobsError: any;
  autojobsSuccess: boolean;
};

export type ExtendedAttributesType = {
  value: {
    autojobsEnabled: boolean;
    [key: string]: any;
  };
};

/**
 * useAutojobsAttribute is a hook that manages the autojobsEnabled attribute of an 
 * Account's extendedAttributes.
 * 
 * Providing that the accountId is not the Root Account ID and that the query is enabled:
 * 
 * - If the autojobsEnabled attribute does not exist, it will be created and set to true.
 * - If the autojobsEnabled attribute exists and is set to true, it will be left as is.
 * - If the autojobsEnabled attribute exists and is set to false, it will be left as is.
 * 
 * ```typescript
 * import useAutojobsAttribute from '@ion/query/src/hooks/useAutojobsAttribute';
 * 
 * const {
 *  autojobsEnabled
 * } = useAutojobsAttribute({
 *  accountId: 8675309,
 *  enabled: true,
 * });
 * 
 * ```
 * 
 * @param accountId - The accountId of the Account to manage the autojobsEnabled attribute for.
 * @param enabled   - Whether or not the autojobsEnabled attribute should be managed.
 * @param page      - The optional page to include in the query key.
 * @param staleTime - The staleTime for the query.
 * 
 * @returns An object containing the autojobsEnabled attribute, loading, error, 
 * and success state.
 */
const useAutojobsAttribute = ({
  accountId,
  enabled = false,
  page = null,
  staleTime = getStaleTime(1, false, false)
}: UseAutojobsAttributeProps): UseAutojobsAttributeResult => {
  const key = useMemo(() => {
    const baseKey = [QueryKey.AUTOJOBS, accountId];
    return page ? [...baseKey, page] : baseKey;
  }, [accountId, page]);

  const queryClient   = useQueryClient();
  const rootAccountId = Number(get('rootAccountId'));
  const disabled      = accountId === rootAccountId || isNil(accountId);

  const query = useQuery(key, async() => {
    if (disabled) {
      return null;
    }

    try {
      const results = (await getAccountExtendedAttributes(accountId)) as ExtendedAttributesType;
      const extendedAttributes = results?.value;

      if (isNil(extendedAttributes?.autojobsEnabled)) {
        const newAttributes = {...extendedAttributes, autojobsEnabled: true};
        await saveAccountExtendedAttributes(accountId, newAttributes, 'put');
        queryClient.setQueryData(key, newAttributes.autojobsEnabled);
        return newAttributes.autojobsEnabled;
      }
      return extendedAttributes?.autojobsEnabled;
    } catch (error) {
      // In the event that we get a 404 because the extendedAttributes do not exist, 
      // we will need to create them.
      if (error?.response?.status === 404) {
        const newAttributes = {autojobsEnabled: true};
        await saveAccountExtendedAttributes(accountId, newAttributes, 'put');
        queryClient.setQueryData(key, newAttributes.autojobsEnabled);
        return newAttributes.autojobsEnabled;
      }
      onClientErrorHandler(error);
    }
  },
  {
    enabled   : enabled && !disabled,
    staleTime : staleTime as number
  }
  );

  return createQueryResult(query, {
    autojobsEnabled : query.data ?? false,
    autojobsLoading : query.isLoading,
    autojobsError   : query.error,
    autojobsSuccess : query.isSuccess
  }) as UseAutojobsAttributeResult;
};

export default useAutojobsAttribute;
